import React, { useEffect, useState } from 'react';
import { Image, View } from 'react-native';
import { showError, showSuccess } from '../../toast';
import { api, API_URL } from '../../api';
import { useAuth } from '../../AuthContext';
import { Button, Card, Input, Label, Muted, Screen, Select, Title } from '../../ui';
import { pickAndUploadPhotos } from '../../photo-picker';
import { t } from '../../i18n';

export default function NewIssueScreen({ navigation, route }: any) {
  // Ако е подадено от управляващия, идва apartmentId (ремонт за конкретен имот,
  // може и без наемател). Наемател с НЯКОЛКО наема избира жилището от списък.
  // Обитателят също подава apartmentId (от „Моят дом“), но НЕ е управляващ —
  // за него текстовете и връщането са като при наемател.
  const routeApartmentId: string | undefined = route?.params?.apartmentId;
  const { user } = useAuth();
  const isManager = user?.role === 'LANDLORD' || user?.role === 'AGENCY';
  const managerMode = !!routeApartmentId && isManager;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [myHomes, setMyHomes] = useState<any[]>([]); // активните наеми на наемателя
  const [pickedApartmentId, setPickedApartmentId] = useState<string | null>(null);
  const apartmentId = routeApartmentId ?? pickedApartmentId ?? undefined;

  useEffect(() => {
    if (routeApartmentId) return; // управляващ — имотът е ясен
    api('/tenant/home')
      .then((r) => {
        const homes = r.tenancies ?? [];
        setMyHomes(homes);
        if (homes.length === 1) setPickedApartmentId(homes[0].apartmentId);
      })
      .catch(() => {});
  }, [routeApartmentId]);

  const pickPhoto = async () => {
    // избор на НЯКОЛКО снимки наведнъж — качват се паралелно
    const urls = await pickAndUploadPhotos();
    if (urls.length) setPhotos((p) => [...p, ...urls]);
  };

  const submit = async () => {
    setLoading(true);
    try {
      await api('/issues', { body: { title: title || undefined, description, photos, apartmentId } });
      showSuccess(managerMode ? t('Заявката за ремонт е записана.') : t('Наемодателят получи известие за нередността.'));
      // Управляващият продължава в „Нередности“ — там се възлага на майстор
      // и се следи статусът. Наемателят/обитателят се връща назад както преди.
      if (managerMode) navigation.replace('Issues');
      else navigation.goBack();
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Screen>
      <Title>{managerMode ? t('Ремонт / нередност') : t('Нова нередност')}</Title>
      <Card>
        {!routeApartmentId && myHomes.length > 1 && (
          <>
            <Label>{t('За кое жилище')}</Label>
            <Select
              placeholder={t('Изберете жилище')}
              options={myHomes.map((h) => `${h.address}, ${h.city}`)}
              value={
                pickedApartmentId
                  ? (() => {
                      const h = myHomes.find((x) => x.apartmentId === pickedApartmentId);
                      return h ? `${h.address}, ${h.city}` : null;
                    })()
                  : null
              }
              onChange={(label: string) => {
                const h = myHomes.find((x) => `${x.address}, ${x.city}` === label);
                if (h) setPickedApartmentId(h.apartmentId);
              }}
            />
          </>
        )}
        <Label>{t('Заглавие (по избор)')}</Label>
        <Input value={title} onChangeText={setTitle} placeholder={t('Течащ кран')} />
        <Label>{t('Описание')}</Label>
        <Input
          value={description}
          onChangeText={setDescription}
          multiline
          numberOfLines={4}
          placeholder={t('Опишете проблема възможно най-точно…')}
          style={{ minHeight: 90, textAlignVertical: 'top' }}
        />
        <Label>{t('Снимки ({n})', { n: photos.length })}</Label>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {photos.map((p) => (
            <Image key={p} source={{ uri: `${API_URL}${p}` }} style={{ width: 72, height: 72, borderRadius: 8 }} />
          ))}
        </View>
        <Button title={t('+ Добави снимка')} variant="secondary" onPress={pickPhoto} />
        {managerMode ? (
          <Muted>{t('Ще можете да възложите ремонта на майстор и да потвърдите извършването му.')}</Muted>
        ) : (
          <Muted>{t('Ще следите статуса: подадена → видяна → възложена → отстранена → потвърдена.')}</Muted>
        )}
        <Button
          title={t('Изпрати')}
          onPress={submit}
          disabled={!description || (!routeApartmentId && myHomes.length > 1 && !pickedApartmentId)}
          loading={loading}
        />
      </Card>
    </Screen>
  );
}
